import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Globe, Leaf } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

const buildLocalizedPath = (lang, subpath) => {
  const clean = subpath && subpath !== "/" ? subpath : "";
  return `/${lang}${clean}`;
};

const stripLangPrefix = (pathname) => {
  if (pathname.startsWith("/fr")) return pathname.slice(3) || "/";
  if (pathname.startsWith("/en")) return pathname.slice(3) || "/";
  return pathname;
};

const isHomePath = (pathname, lang) => {
  const base = buildLocalizedPath(lang, "/");
  return pathname === base || pathname === `${base}/`;
};

export const Header = () => {
  const { t, lang } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // If we land on the home page with a hash (e.g., after navigating from another page),
  // scroll the target section into view. Runs on every location change.
  useEffect(() => {
    if (!isHomePath(location.pathname, lang)) return;
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    // Wait one paint so the target section exists in the DOM
    const timer = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
    return () => window.clearTimeout(timer);
  }, [location.pathname, location.hash, lang]);

  const homePath = buildLocalizedPath(lang, "/");
  const navItems = [
    { key: "home", hash: "", testId: "nav-home" },
    { key: "analysis", hash: "analyse", testId: "nav-analysis" },
    { key: "process", hash: "circuit", testId: "nav-process" },
    { key: "transparency", hash: "transparence", testId: "nav-transparency" },
    { key: "subscribe", hash: "bulletin", testId: "nav-subscribe" },
  ];

  const handleLanguageSwitch = (target) => {
    if (target === lang) return;
    const rest = stripLangPrefix(location.pathname);
    const hash = location.hash || "";
    navigate(`/${target}${rest === "/" ? "" : rest}${hash}`);
  };

  const goToHomeSection = (e, hash) => {
    e.preventDefault();
    setMobileOpen(false);
    const onHome = isHomePath(location.pathname, lang);
    if (!hash) {
      // "Home" link
      if (onHome) {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      } else {
        navigate(homePath);
      }
      return;
    }
    if (onHome) {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      // Keep the URL hash in sync without a full navigation
      window.history.replaceState(null, "", `${homePath}#${hash}`);
    } else {
      navigate(`${homePath}#${hash}`);
    }
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 w-full bg-[#0B1E36] text-white border-b border-slate-800"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8 h-16 lg:h-20">
        <Link
          to={homePath}
          data-testid="header-brand-link"
          className="flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-emerald-400 rounded"
        >
          <span className="flex h-9 w-9 items-center justify-center bg-emerald-600 text-white rounded-sm">
            <Leaf className="h-5 w-5" strokeWidth={2.2} />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-semibold tracking-[0.18em] text-sm sm:text-base">
              {t("header.brand")}
            </span>
            <span className="text-[10px] uppercase tracking-[0.24em] text-emerald-300">
              {t("header.tagline")}
            </span>
          </span>
        </Link>

        <nav aria-label="Primary" className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => (
            <a
              key={item.key}
              href={item.hash ? `${homePath}#${item.hash}` : homePath}
              data-testid={item.testId}
              onClick={(e) => goToHomeSection(e, item.hash)}
              className="text-sm text-slate-200 hover:text-emerald-300 focus:text-emerald-300 focus:outline-none"
            >
              {t(`header.nav.${item.key}`)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            role="group"
            aria-label={t("header.language")}
            className="hidden sm:flex items-center gap-1 border border-slate-700 rounded-sm px-1 py-1"
          >
            <Globe className="h-4 w-4 text-slate-400 mx-1" aria-hidden="true" />
            {["fr", "en"].map((code) => (
              <button
                key={code}
                type="button"
                data-testid={`lang-switch-${code}`}
                aria-pressed={lang === code}
                onClick={() => handleLanguageSwitch(code)}
                className={`px-2 py-1 text-xs font-medium uppercase tracking-widest rounded-sm ${
                  lang === code
                    ? "bg-emerald-600 text-white"
                    : "text-slate-200 hover:text-emerald-300"
                }`}
              >
                {code}
              </button>
            ))}
          </div>

          <button
            type="button"
            data-testid="mobile-menu-toggle"
            aria-expanded={mobileOpen}
            aria-label={mobileOpen ? t("header.menu_close") : t("header.menu_open")}
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-sm border border-slate-700 hover:border-emerald-500"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          data-testid="mobile-menu-panel"
          className="lg:hidden bg-[#0B1E36] border-t border-slate-800"
        >
          <div className="px-4 sm:px-6 py-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <a
                key={`m-${item.key}`}
                href={item.hash ? `${homePath}#${item.hash}` : homePath}
                data-testid={`m-${item.testId}`}
                onClick={(e) => goToHomeSection(e, item.hash)}
                className="py-3 px-2 text-slate-100 hover:text-emerald-300 border-b border-slate-800 text-sm"
              >
                {t(`header.nav.${item.key}`)}
              </a>
            ))}
            <div className="mt-4 flex items-center gap-2">
              <Globe className="h-4 w-4 text-slate-400" aria-hidden="true" />
              <span className="text-xs uppercase tracking-widest text-slate-400 mr-2">
                {t("header.language")}
              </span>
              {["fr", "en"].map((code) => (
                <button
                  key={`m-${code}`}
                  type="button"
                  data-testid={`m-lang-switch-${code}`}
                  aria-pressed={lang === code}
                  onClick={() => handleLanguageSwitch(code)}
                  className={`px-3 py-1.5 text-xs uppercase font-semibold rounded-sm ${
                    lang === code
                      ? "bg-emerald-600 text-white"
                      : "border border-slate-700 text-slate-100"
                  }`}
                >
                  {code}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
